<template>
  <div>
    <b-row v-if="align == 'horizontal'">
      <b-col
        v-for="(filter, index) in filters"
        :key="filter.level"
        cols="12"
        :md="parseInt(12 / filters.length) + parseInt(index == 0? 12 % filters.length : 0)"
        class="mb-md-0 mb-2"
      >
        <label :for="filter.level">{{ filter.label }}</label>
        <b-form-select
          :id="filter.level"
          v-model="filter.location"
          :name="filter.level"
          :size="size"
          @change="changeFilters(filter.location, index)"
        >
          <b-form-select-option :value="null">
            Select...
          </b-form-select-option>
          <b-form-select-option
            v-for="location in filter.locations"
            :key="location.id"
            :value="location"
          >
            {{ location.name.toUpperCase() }}
          </b-form-select-option>
        </b-form-select>
      </b-col>
    </b-row>

    <template v-if="align == 'vertical'">
      <b-row
        v-for="(filter, index) in filters"
        :key="filter.level"
      >
        <b-col
          cols="12"
          class="mb-md-0 mb-2"
        >
          <label :for="filter.level">{{ filter.label }}</label>
          <b-form-select
            :id="filter.level"
            v-model="filter.location"
            :name="filter.level"
            :size="size"
            @change="changeFilters(filter.location, index)"
          >
            <b-form-select-option :value="null">
              Select...
            </b-form-select-option>
            <b-form-select-option
              v-for="location in filter.locations"
              :key="location.id"
              :value="location"
            >
              {{ location.name.toUpperCase() }}
            </b-form-select-option>
          </b-form-select>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import {
    BRow,
    BCol,
    BFormSelect,
    BFormSelectOption,
} from 'bootstrap-vue'
import {
    ref, onUnmounted, onMounted, watch,
} from '@vue/composition-api'
import locationFiltersStoreModule from '@core/components/cromis/filters/locationFiltersStoreModule'
import store from '@/store'

export default {
    components: {
        BRow,
        BCol,
        BFormSelect,
        BFormSelectOption,
    },
    directives: {},
    props: {
        size: {
            type: String,
            required: false,
            default: 'md',
        },
        align: {
            type: String,
            required: false,
            default: 'horizontal',
        },
        selectedLocations: {
            type: Array,
            required: false,
            default: () => [],
        },
    },
    setup(props, context) {
        const storedUserData = JSON.parse(localStorage.getItem('userData') || '{}')
        const normalizeCollection = collection => {
            if (Array.isArray(collection)) return collection
            if (collection && Array.isArray(collection.data)) return collection.data
            if (collection && typeof collection === 'object') return [collection]

            return []
        }
        const filters = ref([])
        const levels = ref(normalizeCollection(storedUserData.levels || (storedUserData.client && storedUserData.client.country ? storedUserData.client.country.levels : [])))
        const locations = ref(normalizeCollection(storedUserData.locations || (storedUserData.client && storedUserData.client.country ? storedUserData.client.country.locations : [])))
        const children = ref([])
        const filtered = ref(null)

        const CROMIS_FILTERS_STORE_MODULE_NAME = 'cromis-filter'

        // Register module
        if (!store.hasModule(CROMIS_FILTERS_STORE_MODULE_NAME)) store.registerModule(CROMIS_FILTERS_STORE_MODULE_NAME, locationFiltersStoreModule)

        // UnRegister on leave
        onUnmounted(() => {
            if (store.hasModule(CROMIS_FILTERS_STORE_MODULE_NAME)) store.unregisterModule(CROMIS_FILTERS_STORE_MODULE_NAME)
        })

        const setFiltersLocations = availableLocations => {
            normalizeCollection(availableLocations).forEach(location => {
                if (!location || !location.level || !location.level.name) return

                const matchedFilter = filters.value.find(filter => location.level.name.toUpperCase() === filter.level.toUpperCase())

                if (matchedFilter) matchedFilter.locations.push(location)
            })
        }

        const buildFilters = () => {
            filters.value = []
            children.value = []

            const topLevel = Array.isArray(levels.value) ? levels.value[0] : levels.value

            if (!topLevel || !topLevel.name) return

            const topLevelChildren = Array.isArray(topLevel.children) ? topLevel.children : []

            filters.value.push({
                level: topLevel.name.toLowerCase(),
                location: null,
                label: topLevel.name,
                child: topLevelChildren.length > 0
                    ? topLevelChildren[0].name.toLowerCase()
                    : null,
                locations: [],
            })

            let list = topLevelChildren

            while (list.length > 0) {
                children.value.push(list[0])

                const childLevels = Array.isArray(list[0].children) ? list[0].children : []

                if (childLevels.length > 0) {
                    filters.value.push({
                        level: list[0].name.toLowerCase(),
                        location: null,
                        label: list[0].name,
                        child: childLevels.length > 0
                            ? childLevels[0].name.toLowerCase()
                            : null,
                        locations: [],
                    })
                } else {
                    filters.value.push({
                        level: list[0].name.toLowerCase(),
                        location: null,
                        label: list[0].name,
                        child: null,
                        locations: [],
                    })
                }

                list = childLevels
            }

            setFiltersLocations(locations.value)
        }

        const applySelectedLocations = selectedLocations => {
            if (!Array.isArray(selectedLocations) || selectedLocations.length === 0) {
                buildFilters()
                filtered.value = null
                return
            }

            buildFilters()

            filters.value.forEach((currentFilter, index) => {
                const selectedLocation = selectedLocations.find(selection => selection.level === currentFilter.level)
                const matchedLocation = selectedLocation
                    ? currentFilter.locations.find(location => location.id === selectedLocation.id)
                    : null

                if (matchedLocation) {
                    filters.value[index].location = matchedLocation

                    if ((index + 1) < filters.value.length) {
                        filters.value[index + 1].locations = matchedLocation.children || []
                    }
                }
            })

            const selectedFilter = [...filters.value].reverse().find(filter => filter.location)
            filtered.value = selectedFilter ? selectedFilter.location : null
        }

        onMounted(() => {
            buildFilters()
            applySelectedLocations(props.selectedLocations)
        })

        watch(() => props.selectedLocations, value => {
            applySelectedLocations(value)
        }, { deep: true })

        const changeFilters = async (filteredLocation, index) => {
            if (!filteredLocation) {
                filters.value.slice(index + 1).forEach((currentFilter, offset) => {
                    const currentFilterIndex = index + 1 + offset

                    filters.value[currentFilterIndex].locations.splice(0)
                    filters.value[currentFilterIndex].location = null
                })

                if (index > 0) {
                    filtered.value = filters.value[index - 1].location
                } else {
                    filtered.value = null
                }
            } else {
                const nextFilter = filters.value[index + 1]
                const childLocations = normalizeCollection(filteredLocation.children)

                if (nextFilter) nextFilter.locations.splice(0)
                if (nextFilter) {
                    childLocations.forEach(child => {
                        nextFilter.locations.push(child)
                    })
                }

                if (nextFilter && childLocations.length > 0) {
                    nextFilter.location = null
                }

                filtered.value = filters.value[index].location
            }

            context.emit('filter', filtered.value)
        }

        return {
            levels,
            filters,
            changeFilters,
            filtered,
            applySelectedLocations,
        }
    },
}
</script>
